import { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from '../services/supabase';
import type { ChatMessage, RealtimeMessagePayload } from '../modules/chat/types';
import { CHAT_CONSTANTS } from '../modules/chat/constants';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface UseRealtimeChatOptions {
  groupId: string;
  onMessageReceived: (message: ChatMessage) => void;
  onMessageUpdated?: (message: ChatMessage) => void;
  onMessageDeleted?: (messageId: string) => void;
  enabled?: boolean;
}

/**
 * Supabase Realtime 聊天 Hook
 * 
 * 功能：
 * - 订阅群聊消息的实时更新
 * - 自动处理新消息、更新、删除事件
 * - 自动清理订阅
 * 
 * @example
 * ```typescript
 * const { isConnected, error } = useRealtimeChat({
 *   groupId: 'group-123',
 *   onMessageReceived: (message) => {
 *     console.log('New message:', message);
 *   },
 * });
 * ```
 */
export function useRealtimeChat({
  groupId,
  onMessageReceived,
  onMessageUpdated,
  onMessageDeleted,
  enabled = true,
}: UseRealtimeChatOptions) {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 获取用户资料
  const fetchUserProfile = useCallback(async (userId: string) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('display_name, avatar_url')
        .eq('id', userId)
        .single();
      
      return data || { display_name: null, avatar_url: null };
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      return { display_name: null, avatar_url: null };
    }
  }, []);

  // 处理新消息
  const handleInsert = useCallback(async (payload: any) => {
    try {
      const newMessage = payload.new;
      
      // 获取用户资料
      const userProfile = await fetchUserProfile(newMessage.user_id);
      
      const message: ChatMessage = {
        ...newMessage,
        user_profile: userProfile,
      };
      
      onMessageReceived(message);
    } catch (error) {
      console.error('Error handling new message:', error);
    }
  }, [onMessageReceived, fetchUserProfile]);

  // 处理消息更新
  const handleUpdate = useCallback(async (payload: any) => {
    if (!onMessageUpdated) return;
    
    try {
      const updatedMessage = payload.new;
      const userProfile = await fetchUserProfile(updatedMessage.user_id);
      
      const message: ChatMessage = {
        ...updatedMessage,
        user_profile: userProfile,
      };
      
      onMessageUpdated(message);
    } catch (error) {
      console.error('Error handling message update:', error);
    }
  }, [onMessageUpdated, fetchUserProfile]);

  // 处理消息删除
  const handleDelete = useCallback((payload: any) => {
    if (!onMessageDeleted) return;
    
    try {
      const deletedMessage = payload.old;
      onMessageDeleted(deletedMessage.id);
    } catch (error) {
      console.error('Error handling message delete:', error);
    }
  }, [onMessageDeleted]);

  useEffect(() => {
    if (!enabled || !groupId) {
      return;
    }

    // 清理旧的订阅
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // 创建新的 channel
    const channelName = `${CHAT_CONSTANTS.REALTIME.CHANNEL_PREFIX}${groupId}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: CHAT_CONSTANTS.REALTIME.TABLE,
          filter: `group_id=eq.${groupId}`,
        },
        handleInsert
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: CHAT_CONSTANTS.REALTIME.TABLE,
          filter: `group_id=eq.${groupId}`,
        },
        handleUpdate
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: CHAT_CONSTANTS.REALTIME.TABLE,
          filter: `group_id=eq.${groupId}`,
        },
        handleDelete
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsConnected(true);
          setError(null);
          console.log(`✅ Realtime connected to ${channelName}`);
        } else if (status === 'CHANNEL_ERROR') {
          setIsConnected(false);
          setError('Failed to connect to realtime');
          console.error(`❌ Realtime connection error for ${channelName}`);
        } else if (status === 'TIMED_OUT') {
          setIsConnected(false);
          setError('Connection timed out');
          console.error(`⏱️ Realtime connection timeout for ${channelName}`);
        }
      });

    channelRef.current = channel;

    // 清理函数
    return () => {
      if (channelRef.current) {
        console.log(`🔌 Disconnecting from ${channelName}`);
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
        setIsConnected(false);
      }
    };
  }, [enabled, groupId, handleInsert, handleUpdate, handleDelete]);

  return {
    isConnected,
    error,
  };
}
